import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, CheckCircle, AlertTriangle, History, FileText, X, Flag } from 'lucide-react';
import useQuizStore from '../../store/quizStore';
import useAuthStore from '../../store/authStore';
import api from '../../services/api';
import QuestionCard from '../../components/quiz/QuestionCard';
import ProgressBar from '../../components/ui/ProgressBar';
import RichContent from '../../components/content/RichContent';
import QuizReviewPanel from '../../components/quiz/QuizReviewPanel';
import QuestionEditPanel from '../../components/quiz/QuestionEditPanel';
import DocumentViewer from '../../components/ui/DocumentViewer';
import SujetDocsButton from '../../components/custom/SujetDocsButton';
import ThemeToggle from '../../components/ui/ThemeToggle';
import MasteryVerdict from '../../components/quiz/MasteryVerdict';
import { loginUrl } from '../../utils/loginRedirect';

// Suivi de contenu (migration 180) : la question a changé APRÈS la séance relue (marqueur posé par startReview).
function ContentChangeNotice({ change }) {
    if (!change?.kind || !change.date) return null;
    const date = new Date(String(change.date).replace(' ', 'T')).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    const note = change.note ? ` (${change.note})` : '';
    return change.kind === 'corrigee' ? (
        <div className="quiz-version-notice">
            <History size={13} />
            <span>Question <strong>corrigée le {date}</strong>, après ta séance{note}. La correction affichée est la version actuelle.</span>
        </div>
    ) : (
        <div className="quiz-version-notice quiz-version-notice--amelioree">
            <History size={13} />
            <span>Question <strong>améliorée le {date}</strong>{note}. Les réponses ou l'explication peuvent différer de ce que tu avais vu.</span>
        </div>
    );
}

// afterExplanation(question) : contenu facultatif affiché sous l'explication (notions : « Revoir dans la fiche »).
export default function Quiz({ sousSujetIdProp, scopeIdProp, scopeInfoProp, nbQuestionsProp, modeProp, questionIdsProp, originProp, isCustomProp, isPartnerProp, onClose, verdictAction, verdictUpsell, afterExplanation }) {
    const { sousSujetId: sousSujetIdParam } = useParams();
    const navigate = useNavigate();
    const timerRef = useRef(null);
    const dotsRef = useRef(null);
    const reviewDotsRef = useRef(null);
    const finishingRef = useRef(false);
    const closingRef = useRef(false);
    // « S'arrêter là » demandé : un nouvel essai d'enregistrement reste partiel.
    const stopRequestedRef = useRef(false);
    const [closing, setClosing] = useState(false);

    const sousSujetId = sousSujetIdProp || sousSujetIdParam;
    const isModal = !!onClose;
    const location = useLocation();
    const { user } = useAuthStore();
    const isReviewer = user?.role === 'admin' || user?.role === 'reviewer';

    // Route privée /compte/quiz/... → exige login (préserve l'URL pour le retour).
    // /:scope/quiz/:id (route publique demo) reste accessible sans compte.
    const requiresAuth = !isModal && location.pathname.startsWith('/compte/');
    if (requiresAuth && !user) return <Navigate to={loginUrl()} replace />;

    const {
        quizInfo, questions, answers, correctAnswers, responseTimes,
        currentIndex, timeInSeconds, loading, finished, finalizing, isReview, isSmartQuiz, mode, origin,
        startQuiz, setAnswer, nextQuestion, prevQuestion,
        goToQuestion, finishQuiz, finishSmartQuiz, tick, reset, docsMap, accessDenied, updateQuestion, removeQuestion,
        error: quizError, questionSource, timeLimit, targetPct, plannedTotal,
    } = useQuizStore();
    // Options du Mélange en classique (migration 261) : temps max et objectif de score, choisis par le joueur.
    const melangeOptions = origin === 'melange' && mode === 'quiz';
    const activeLimit = melangeOptions ? timeLimit : null;
    const activeTarget = melangeOptions ? targetPct : null;
    // Outils de relecture/édition ÉDITORIAUX (get_question, review_stats, QuestionEditPanel 'official') :
    // seulement sur des questions éditoriales. Les ids de user_questions / partner_questions recouvrent
    // ceux de `questions` : ailleurs (perso, communautaire, y compris quiz intelligent/examen sans
    // isCustomProp) ils viseraient la question éditoriale de même id.
    const editorialTools = isReviewer && questionSource === 'official' && !isPartnerProp && !isCustomProp;
    const answeredCount = Object.keys(answers).length;
    const allAnswered = questions.every(q => !!answers[q.id]);
    const nbAnswered = questions.filter(q => !!answers[q.id]).length;

    // Track which questions have been answered in apprentissage mode (for showing feedback)
    const [feedbackShown, setFeedbackShown] = useState({});
    // Navigation index for finished screen (review inline)
    const [reviewIndex, setReviewIndex] = useState(0);
    const [showDocViewer, setShowDocViewer] = useState(false);
    // Close doc viewer when navigating to another question
    useEffect(() => { setShowDocViewer(false); }, [currentIndex]);
    // Question suivante : si le début de la carte est sorti de l'écran (question longue, surtout sur mobile),
    // on y remonte ; sinon on ne bouge pas (sur ordinateur la carte est en général déjà visible).
    const prevIndexRef = useRef(currentIndex);
    useEffect(() => {
        if (prevIndexRef.current === currentIndex) return;
        prevIndexRef.current = currentIndex;
        const id = requestAnimationFrame(() => {
            const card = document.querySelector('.question-card');
            if (card && card.getBoundingClientRect().top < 0) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return () => cancelAnimationFrame(id);
    }, [currentIndex]);
    // Inactivity pause
    const [paused, setPaused] = useState(false);
    // Célébration de fin de quiz (anneau animé + confettis) — classique ET apprentissage.
    const [showCelebration, setShowCelebration] = useState(false);
    // Revue filtrée sur les erreurs (déclenchée depuis le verdict ou la bascule).
    const [errOnly, setErrOnly] = useState(false);
    const celebratedRef = useRef(false);
    useEffect(() => {
        if (finished && !isReview && !celebratedRef.current) {
            celebratedRef.current = true;
            setShowCelebration(true);
        }
        if (!finished) { celebratedRef.current = false; setErrOnly(false); }
    }, [finished, isReview]);
    // Consultation d'une séance dont une partie n'est plus accessible (abonnement terminé) : la consultation
    // reste ouverte (séance payée), on dit seulement ce qui reste rejouable (27/10/2026, décision de Fabien).
    const [accesRevue, setAccesRevue] = useState(null); // { playable, total, locked }
    useEffect(() => {
        setAccesRevue(null);
        const scope = scopeIdProp || useQuizStore.getState().scopeId;
        if (!isReview || !questions.length || !scope) return;
        let actif = true;
        const ids = questions.map(q => q.id);
        api.post('/qcm/replay-scope.php', { scope_id: scope, question_ids: ids })
            .then(({ data }) => { if (actif) setAccesRevue({ playable: data.data.playable.length, total: data.data.total, locked: data.data.locked }); })
            .catch(err => { if (actif && err?.response?.status === 403) setAccesRevue({ playable: 0, total: ids.length, locked: ids.length }); });
        return () => { actif = false; };
    }, [isReview, questions.length]);
    const allerAbonnement = () => {
        const slug = scopeInfoProp?.slug;
        if (!slug) return;
        if (isModal) onClose();
        reset();
        navigate(`/compte/quiz/${slug}/abonnement`);
    };
    // Rythme des autres utilisateurs sur les mêmes questions (verdict : « ton rythme / celui des autres »).
    // Questions éditoriales seulement : les ids perso / communautaires recouvrent ceux de `questions`.
    const [paceOthers, setPaceOthers] = useState(null);
    useEffect(() => {
        setPaceOthers(null);
        if (!showCelebration || questionSource !== 'official' || isCustomProp || isPartnerProp) return undefined;
        const ids = questions.filter(q => responseTimes?.[q.id]).map(q => q.id);
        if (!ids.length) return undefined;
        let alive = true;
        api.post('/qcm/pace.php', { question_ids: ids })
            .then(({ data }) => { if (alive) setPaceOthers(data.data?.others ?? null); })
            .catch(() => {});
        return () => { alive = false; };
    }, [showCelebration]); // eslint-disable-line react-hooks/exhaustive-deps

    // Popup de validation quand toutes les questions viennent d'être répondues
    // (mode classique uniquement) — proposé une fois, à la 1re complétion.
    const [showValidatePrompt, setShowValidatePrompt] = useState(false);
    // Temps max atteint : proposé une fois par affichage du quiz (valider ou poursuivre).
    const [showTimeUp, setShowTimeUp] = useState(false);
    // « S'arrêter là » demande confirmation : bouton proche de la navigation, une fausse manip clôturait la séance.
    const [showStopConfirm, setShowStopConfirm] = useState(false);
    const timeUpShownRef = useRef(false);
    // Mobile : la rangée des numéros de questions est repliée derrière « Questions » (audit mobile 18/10).
    const [dotsOpen, setDotsOpen] = useState(false);
    const validatePromptRef = useRef(false);
    useEffect(() => {
        if (finished || isReview || mode === 'apprentissage') return;
        const total = questions.length;
        if (total === 0) return;
        const nbAns = Object.values(answers).filter(Boolean).length;
        if (nbAns >= total) {
            if (!validatePromptRef.current) {
                validatePromptRef.current = true;
                setShowValidatePrompt(true);
            }
        } else {
            validatePromptRef.current = false;
        }
    }, [answers, questions.length, finished, isReview, mode]);
    const inactivityRef = useRef(null);
    const INACTIVITY_DELAY = 60_000; // 60 seconds

    // Stats de reviews pour le sous-sujet (admin/reviewer uniquement)
    const [reviewStats, setReviewStats] = useState(null);

    // Auto-scroll dots to center the active dot (robust on mobile: waits a frame,
    // checks real visibility via getBoundingClientRect — not offsetLeft which can
    // be stale right after re-render on small screens).
    const scrollDotsToIndex = useCallback((containerRef, idx) => {
        requestAnimationFrame(() => {
            const container = containerRef.current;
            if (!container) return;
            const btn = container.children[idx];
            if (!btn) return;

            const cRect = container.getBoundingClientRect();
            const bRect = btn.getBoundingClientRect();
            // Fully visible ? (small tolerance for borders)
            const fullyVisible = bRect.left >= cRect.left - 1 && bRect.right <= cRect.right + 1;
            if (fullyVisible) return;

            // Center the dot in the viewport of the scrollable container.
            const offset = (bRect.left - cRect.left) - (container.clientWidth / 2 - btn.offsetWidth / 2);
            container.scrollBy({ left: offset, behavior: 'smooth' });
        });
    }, []);

    // Scroll arrows helper
    const scrollDots = useCallback((containerRef, direction) => {
        const container = containerRef.current;
        if (!container) return;
        container.scrollBy({
            left: direction === 'left' ? -container.clientWidth : container.clientWidth,
            behavior: 'smooth'
        });
    }, []);

    const isApprentissage = mode === 'apprentissage';

    const handleBack = async () => {
        const initial = useQuizStore.getState();
        if (closingRef.current || initial.finalizing || finishingRef.current) return;
        const isCurrent = () => useQuizStore.getState()._runId === initial._runId
            && useQuizStore.getState().quizInfo === initial.quizInfo;
        closingRef.current = true;
        setClosing(true);
        try {
            clearInterval(timerRef.current);
            clearTimeout(initial._saveTimer);
            // Toutes les sorties de la modale attendent la persistance.
            if (!initial.finished && initial.questions.length > 0 && initial.questions.every(q => !!initial.answers[q.id])) {
                if (initial.isSmartQuiz) await finishSmartQuiz();
                else await finishQuiz();
                if (!isCurrent() || !useQuizStore.getState().finished) return;
            } else if (!initial.finished && !initial.isReview && initial.questions.length > 0) {
                await useQuizStore.getState()._saveSessionNow();
            }
            if (!isCurrent()) return;
            if (isModal) {
                onClose();
                reset();
            } else {
                reset();
                navigate(-1);
            }
        } catch {
            if (isCurrent()) useQuizStore.setState({ error: 'Impossible de sauvegarder ta séance. Réessaie avant de quitter.' });
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    };

    // Annuler la séance tant qu'aucune réponse n'est donnée (29/10/2026) : rien n'est enregistré ni perdu.
    // Le serveur ne supprime la séance que si elle est encore vide (only_if_empty) ; s'il refuse (réponse
    // enregistrée depuis un autre appareil), la séance reste reprenable. On sort ensuite sans sauvegarder.
    const handleCancelEmpty = async () => {
        const initial = useQuizStore.getState();
        if (closingRef.current || initial.finalizing || finishingRef.current) return;
        if (Object.keys(initial.answers || {}).length > 0) return;
        closingRef.current = true;
        setClosing(true);
        try {
            clearInterval(timerRef.current);
            clearTimeout(initial._saveTimer);
            if (initial.sessionId) {
                try {
                    await api.delete('/qcm/session.php', { data: { session_id: initial.sessionId, only_if_empty: true } });
                } catch (e) { console.warn('Annulation de séance refusée :', e); }
            }
            if (isModal) {
                onClose();
                reset();
            } else {
                reset();
                navigate(-1);
            }
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    };

    // Demarrer le quiz (sauf si smart quiz deja charge)
    useEffect(() => {
        if ((isSmartQuiz || isReview) && questions.length > 0) return;
        if (!sousSujetId) return;
        const init = async () => {
            const ok = await startQuiz(
                parseInt(sousSujetId),
                scopeIdProp,
                nbQuestionsProp || 10,
                modeProp || 'quiz',
                questionIdsProp || null,
                originProp || 'subject',
                { isCustom: !!isCustomProp, isPartner: !!isPartnerProp }
            );
            if (!ok) handleBack();
        };
        init();
        return () => {
            clearInterval(timerRef.current);
        };
    }, [sousSujetId]);

    // Restore feedbackShown for apprentissage mode when resuming a session
    useEffect(() => {
        if (isApprentissage && Object.keys(answers).length > 0) {
            const restored = {};
            for (const qId of Object.keys(answers)) {
                if (answers[qId]) restored[qId] = true;
            }
            setFeedbackShown(restored);
        }
    }, [loading]);

    // Auto-finish à la reprise d'une session déjà entièrement répondue —
    // UNIQUEMENT en apprentissage (aucune validation à ce mode).
    // En classique (dont l'audit du parcours), on NE valide PAS d'office : on laisse
    // le popup de validation s'afficher pour permettre de revoir / modifier d'abord.
    useEffect(() => {
        if (mode === 'apprentissage' && !loading && !finished && questions.length > 0) {
            const answeredCount = Object.values(answers).filter(Boolean).length;
            if (answeredCount >= questions.length) {
                handleFinish();
            }
        }
    }, [loading, questions.length, mode]);

    // Save before page close/refresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (!finished && !isReview && questions.length > 0) {
                useQuizStore.getState()._autoSave();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [finished, isReview, questions.length]);

    // Timer — pauses when inactive
    useEffect(() => {
        if (!loading && !finished && !finalizing && !closing && !isReview && questions.length > 0 && !paused) {
            timerRef.current = setInterval(tick, 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [loading, finished, finalizing, closing, isReview, questions.length, paused]);

    // Temps max atteint : on propose de valider ou de poursuivre (le dépassement s'affiche ensuite).
    useEffect(() => {
        if (!activeLimit || finished || finalizing || isReview || loading || timeUpShownRef.current) return;
        if (timeInSeconds >= activeLimit) {
            timeUpShownRef.current = true;
            setShowValidatePrompt(false);
            setShowTimeUp(true);
        }
    }, [activeLimit, timeInSeconds, finished, finalizing, isReview, loading]);

    // Inactivity detection — pause after 60s of no interaction
    useEffect(() => {
        if (loading || finished || isReview || questions.length === 0) return;

        const resetInactivity = () => {
            if (paused) setPaused(false);
            clearTimeout(inactivityRef.current);
            inactivityRef.current = setTimeout(() => setPaused(true), INACTIVITY_DELAY);
        };

        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
        events.forEach(e => document.addEventListener(e, resetInactivity, { passive: true }));
        // Start the timer
        inactivityRef.current = setTimeout(() => setPaused(true), INACTIVITY_DELAY);

        return () => {
            events.forEach(e => document.removeEventListener(e, resetInactivity));
            clearTimeout(inactivityRef.current);
        };
    }, [loading, finished, isReview, questions.length]);

    // Auto-scroll dots containers when active index changes
    useEffect(() => { scrollDotsToIndex(dotsRef, currentIndex); }, [currentIndex]);
    useEffect(() => { scrollDotsToIndex(reviewDotsRef, reviewIndex); }, [reviewIndex]);

    // Fetch stats de reviews pour le sous-sujet (admin/reviewer, quiz classique uniquement)
    useEffect(() => {
        if (!editorialTools || !quizInfo?.id || isSmartQuiz) return;
        setReviewStats(null);
        api.post('/admin/questions.php', { action: 'review_stats', sous_sujet_id: quizInfo.id })
            .then(({ data }) => setReviewStats(data.data))
            .catch(e => console.warn("Erreur:", e));
    }, [quizInfo?.id, editorialTools, isSmartQuiz]);

    // Anti copy-paste protection (not for admin/reviewer)
    useEffect(() => {
        if (isReviewer) return;
        const prevent = (e) => {
            const el = e.target;
            if (el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable || el?.closest?.('[contenteditable]')) return;
            e.preventDefault();
        };
        document.addEventListener('copy', prevent);
        document.addEventListener('cut', prevent);
        document.addEventListener('paste', prevent);
        document.addEventListener('contextmenu', prevent);
        return () => {
            document.removeEventListener('copy', prevent);
            document.removeEventListener('cut', prevent);
            document.removeEventListener('paste', prevent);
            document.removeEventListener('contextmenu', prevent);
        };
    }, [isReviewer]);

    // Lock body scroll when quiz is active (prevents background scrolling)
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    // Convert internal reponse ID to display label (A/B/C/D) based on shuffled order
    const getDisplayLabel = (question, reponseId) => {
        const active = (question.reponses || []).filter(r => r.isActive !== false);
        const idx = active.findIndex(r => r.id === reponseId);
        return idx >= 0 ? String.fromCharCode(65 + idx) : '—';
    };

    if (loading) {
        return <div className="quiz-loading">Chargement du quiz...</div>;
    }

    if (quizError) {
        return (
            <div className="quiz-empty quiz-access-denied">
                <AlertTriangle size={40} style={{ color: '#dc2626', marginBottom: 12 }} />
                <h3>Une erreur est survenue</h3>
                <p>{quizError}</p>
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    {isSmartQuiz && questions.length > 0 && !finished && <button className="btn btn-success" disabled={closing || finalizing} onClick={() => allAnswered ? finishSmartQuiz() : stopRequestedRef.current ? finishSmartQuiz({ partial: true }) : handleBack()}>Réessayer l’enregistrement</button>}
                    <button className="btn btn-primary" disabled={closing || finalizing} onClick={handleBack}>Retour</button>
                </div>
            </div>
        );
    }

    if (accessDenied) {
        const scopeSlug = useQuizStore.getState().scopeId;
        return (
            <div className="quiz-empty quiz-access-denied">
                <AlertTriangle size={40} style={{ color: '#f59e0b', marginBottom: 12 }} />
                <h3>Abonnement expiré</h3>
                <p>Votre accès à cette thématique a expiré. Renouvelez votre abonnement pour continuer.</p>
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    {isModal ? (
                        <button className="btn btn-primary" onClick={onClose}>Retour</button>
                    ) : (
                        <button className="btn btn-primary" onClick={() => navigate(-1)}>Retour</button>
                    )}
                </div>
            </div>
        );
    }

    if (!questions.length) {
        // Clôture d'une séance dont toutes les questions ont été retirées entre-temps.
        if (finished) return (
            <div className="quiz-empty">
                <p>Séance close : ses questions ont été retirées entre-temps. Aucun résultat n’est enregistré.</p>
                <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={handleBack}>Retour</button>
            </div>
        );
        return <div className="quiz-empty">Aucune question disponible.</div>;
    }

    const safeIndex = Math.min(currentIndex, questions.length - 1);
    const currentQuestion = questions[safeIndex];
    if (!currentQuestion) return <div className="quiz-empty">Erreur : question introuvable.</div>;
    const currentAnswered = !!answers[currentQuestion.id];
    const currentFeedback = !!feedbackShown[currentQuestion.id];

    // --- Revue : navigation filtrée (toutes les questions, ou seulement les erreurs) ---
    const inReviewMode = finished || isReview;
    const wrongIndices = inReviewMode && correctAnswers
        ? questions.reduce((acc, q, i) => {
            if (correctAnswers[q.id]?.correct !== answers[q.id]) acc.push(i);
            return acc;
        }, [])
        : [];
    // Liste réellement parcourue par les dots + flèches en revue.
    const reviewIndices = (errOnly && wrongIndices.length > 0) ? wrongIndices : questions.map((_, i) => i);
    const reviewPos = reviewIndices.indexOf(safeIndex);
    const atReviewStart = reviewPos === 0;
    const atReviewEnd = reviewPos === reviewIndices.length - 1;
    const goReviewPrev = () => {
        if (reviewPos === -1) { if (reviewIndices.length) goToQuestion(reviewIndices[0]); return; }
        if (reviewPos > 0) goToQuestion(reviewIndices[reviewPos - 1]);
    };
    const goReviewNext = () => {
        if (reviewPos === -1) { if (reviewIndices.length) goToQuestion(reviewIndices[0]); return; }
        if (reviewPos < reviewIndices.length - 1) goToQuestion(reviewIndices[reviewPos + 1]);
    };

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    // Bilan des options du Mélange (objectif de score, temps max) : sous l'en-tête ET dans la fenêtre de résultat.
    const renderObjectifBilan = (inPopup = false) => {
        if (!finished || !correctAnswers || !(activeTarget || activeLimit)) return null;
        const score = Object.entries(answers).filter(([qId]) => correctAnswers[qId]?.correct === answers[qId]).length;
        const pct = Math.round((score / questions.length) * 100);
        // Séance arrêtée avant la fin : le score ne porte que sur les questions répondues,
        // l'objectif n'est donc pas évalué (1 bonne réponse sur 1 ne vaut pas 100 % de la séance).
        const incomplete = plannedTotal && plannedTotal > questions.length;
        const reached = activeTarget && !incomplete && pct >= activeTarget;
        return (
            <div className={`quiz-objectif ${inPopup ? 'quiz-objectif--popup' : ''} ${activeTarget && !incomplete ? (reached ? 'is-reached' : 'is-missed') : ''}`}>
                {activeTarget && incomplete && (
                    <span>🎯 Objectif {activeTarget}{' '}% : <strong>non évalué</strong> (séance arrêtée à {questions.length} question{questions.length > 1 ? 's' : ''} sur {plannedTotal})</span>
                )}
                {activeTarget && !incomplete && (
                    <span>🎯 Objectif {activeTarget}{' '}% : <strong>{reached ? 'atteint' : 'pas encore'}</strong> ({pct}{' '}%)</span>
                )}
                {activeLimit && (
                    <span>⏱ {formatTime(timeInSeconds)} sur {formatTime(activeLimit)}
                        {timeInSeconds > activeLimit && <> · <strong>dépassé de {formatTime(timeInSeconds - activeLimit)}</strong></>}
                    </span>
                )}
            </div>
        );
    };


    const handleAnswer = (questionId, answer) => {
        if (closingRef.current || useQuizStore.getState().finalizing) return;
        // In apprentissage mode, block re-answering once feedback is shown
        if (isApprentissage && feedbackShown[questionId]) return;

        setAnswer(questionId, answer);

        // In classic mode, auto-advance after a short delay
        if (!isApprentissage && !finished) {
            const runId = useQuizStore.getState()._runId;
            setTimeout(() => {
                const state = useQuizStore.getState();
                if (closingRef.current || state._runId !== runId || state.finished || state.finalizing) return;
                const { currentIndex: idx, questions: qs } = state;
                if (idx < qs.length - 1) {
                    nextQuestion();
                }
            }, 400);
        }

        // In apprentissage mode, show feedback immediately
        if (isApprentissage) {
            setFeedbackShown(prev => ({ ...prev, [questionId]: true }));
            // Auto-scroll to explication after a short delay
            setTimeout(() => {
                document.querySelector('.explication')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 350);
        }
    };

    // partial = « S'arrêter là » : enregistre les seules questions répondues.
    const handleFinish = async (partial = false) => {
        if (closingRef.current) return;
        if (finished || finishingRef.current) return;
        finishingRef.current = true;
        stopRequestedRef.current = partial;
        clearInterval(timerRef.current);
        try {
            if (isSmartQuiz) {
                await finishSmartQuiz({ partial });
            } else {
                await finishQuiz({ partial });
            }
            if (!useQuizStore.getState().finished) return;
            // Apprentissage : rester sur la derniere question repondue (le feedback est deja visible).
            // Quiz classique : retour a la Q1 pour le review sequentiel.
            if (!isApprentissage) {
                goToQuestion(0);
            }
        } finally {
            finishingRef.current = false;
        }
    };

    // Should we show the result for the current question?
    const showCurrentResult = isReview || finished || (isApprentissage && currentFeedback);

    // In apprentissage mode, skip the finished screen — stay on quiz for navigation
    if (finished && !isReview) {
        // Don't show finished screen — fall through to the normal quiz view
        // Both classique and apprentissage use the same review mode:
        // Navigate questions with correct/incorrect feedback inline
    }

    // Legacy: this block is now skipped (unified review mode above)
    if (false && finished && !isApprentissage && !isReview) {
        const score = Object.entries(answers).filter(([qId]) =>
            correctAnswers?.[qId]?.correct === answers[qId]
        ).length;
        const pct = Math.round((score / questions.length) * 100);

        const rq = questions[reviewIndex];
        const rGiven = answers[rq.id] || null;
        const rCorrect = correctAnswers?.[rq.id]?.correct;
        const rIsCorrect = rGiven === rCorrect;
        const rExplication = correctAnswers?.[rq.id]?.explication;

        return (
            <div className={`quiz-finished ${isReviewer ? '' : 'no-select'}`}>
                {/* Mini-nav slider sticky top */}
                <div className="quiz-mini-nav">
                    <button className="mini-nav-arrow" onClick={() => scrollDots(reviewDotsRef, 'left')}>
                        <ChevronLeft size={18} />
                    </button>
                    <div className="mini-nav-dots" ref={reviewDotsRef}>
                        {questions.map((q, i) => {
                            const qCorrect = correctAnswers?.[q.id]?.correct === answers[q.id];
                            return (
                                <button
                                    key={q.id}
                                    className={`dot ${i === reviewIndex ? 'active' : ''} ${qCorrect ? 'correct' : 'incorrect'}`}
                                    onClick={() => setReviewIndex(i)}
                                >
                                    {i + 1}
                                </button>
                            );
                        })}
                    </div>
                    <button className="mini-nav-arrow" onClick={() => scrollDots(reviewDotsRef, 'right')}>
                        <ChevronRight size={18} />
                    </button>
                </div>

                <h2>Quiz terminé !</h2>
                <div className="quiz-finished-mode">
                    <span className={`quiz-mode-badge ${mode}`}>
                        {isApprentissage ? 'Apprentissage' : 'Classique'}
                    </span>
                </div>
                <div className="quiz-score-summary">
                    <span className={`score-value ${pct >= 70 ? 'good' : pct >= 50 ? 'ok' : 'bad'}`}>
                        {score}/{questions.length}
                    </span>
                    <span className="score-pct">{pct}%</span>
                    <span className="score-time">Temps : {formatTime(timeInSeconds)}</span>
                </div>

                <div className={`quiz-review-item ${rIsCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="quiz-review-header">
                        <span className="q-num">Q{reviewIndex + 1}</span>
                        <span className={`q-status ${rIsCorrect ? 'correct' : 'incorrect'}`}>
                            {rIsCorrect ? 'Correct' : 'Incorrect'}
                        </span>
                    </div>
                    <ContentChangeNotice change={rq._contentChange} />
                    {rq._versionChanged && (
                        <div className="quiz-version-notice">
                            <History size={13} />
                            Version répondue : v{rq._answeredVersion} — cette question a été modifiée depuis (v{rq._currentVersion} actuelle)
                        </div>
                    )}
                    <QuestionCard
                        question={rq}
                        selectedAnswer={rGiven}
                        onAnswer={() => {}}
                        showResult={true}
                        correctAnswer={rCorrect}
                    />
                    {!rIsCorrect && (
                        <p className="quiz-review-correction">
                            Votre réponse : <strong>{getDisplayLabel(rq, rGiven)}</strong>
                            {' — '}
                            Bonne réponse : <strong>{getDisplayLabel(rq, rCorrect)}</strong>
                        </p>
                    )}
                    {rExplication && (
                        <div className="explication">
                            <h4>Explication</h4>
                            <RichContent blocks={rExplication} />
                        </div>
                    )}
                    {rExplication && afterExplanation?.(rq)}
                    {(isCustomProp || editorialTools) && (
                        <QuestionEditPanel
                            question={rq}
                            correctData={correctAnswers?.[rq.id]}
                            source={isCustomProp ? 'custom' : 'official'}
                            onSaved={(updated) => updateQuestion(rq.id, updated)}
                            onDeleted={isCustomProp ? (id) => removeQuestion(id) : undefined}
                        />
                    )}
                    {editorialTools && <QuizReviewPanel question={rq} user={user} />}
                </div>

                <div className="quiz-nav">
                    <button
                        className="btn btn-secondary"
                        onClick={() => setReviewIndex(i => i - 1)}
                        disabled={reviewIndex === 0}
                    >
                        Précédent
                    </button>
                    {reviewIndex < questions.length - 1 ? (
                        <button className="btn btn-primary" onClick={() => setReviewIndex(i => i + 1)}>
                            Suivant
                        </button>
                    ) : (
                        <button className="btn btn-secondary" onClick={handleBack}>
                            Retour
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const scopeBanner = scopeInfoProp || null;
    // En mode transverse (mélange/audit/smart/erreurs), la bannière suit la matière
    // de la question courante (si dispo) ; sinon image du scope, fixe.
    // La consultation d'une séance transverse (Quiz intelligent, Mélange…) n'a pas l'origine de la séance :
    // on la reconnaît à ses questions venues de plusieurs sujets (27/10/2026, catégorie figée sinon).
    const multiSujets = new Set(questions.map(q => q.sous_sujet_id).filter(Boolean)).size > 1;
    const isTransversalBanner = (origin && origin !== 'subject' && !origin.startsWith('history:')) || multiSujets;
    const bannerCategorie = (isTransversalBanner && questions[safeIndex]?.categorie) || quizInfo?.categorie || null;
    const currentMatiereImage = isTransversalBanner ? (questions[safeIndex]?.matiere_image || null) : null;
    const bannerImage = currentMatiereImage || scopeBanner?.image || null;
    const scopeAccent = scopeBanner?.couleur_theme || null;

    return (
        <>
        {/* Close button + theme toggle — fixed, outside quiz-page to avoid mask-image clipping */}
        {isModal && (
            <>
                <ThemeToggle className="quiz-theme-toggle quiz-theme-toggle--right" />
                <button className="quiz-modal-close quiz-modal-close--fixed" disabled={closing || finalizing} onClick={handleBack} title={closing ? 'Sauvegarde en cours' : finalizing ? 'Enregistrement du résultat en cours' : 'Fermer'}>
                    <X size={22} strokeWidth={2} />
                </button>
            </>
        )}
        {showCelebration && (() => {
            // Diagnostic calculé localement (zéro appel backend) à partir de la
            // session : score, regroupement par sous-sujet (diagnostic transverse),
            // temps moyen via responseTimes, première erreur pour le CTA ciblé.
            const groups = {};
            let cScore = 0, timeSum = 0, timeCount = 0, firstWrong = -1;
            questions.forEach((q, i) => {
                const ok = !!correctAnswers && correctAnswers[q.id]?.correct === answers[q.id];
                if (ok) cScore++;
                else if (firstWrong === -1) firstWrong = i;
                const ssId = q.sous_sujet_id || 'main';
                const label = q.sous_sujet_label || quizInfo?.label || 'Ce sujet';
                if (!groups[ssId]) groups[ssId] = { label, total: 0, correct: 0 };
                groups[ssId].total++; if (ok) groups[ssId].correct++;
                const rt = responseTimes?.[q.id];
                if (rt) { timeSum += rt; timeCount++; }
            });
            const cTotal = questions.length;
            const cPct = cTotal ? Math.round((cScore / cTotal) * 100) : 0;
            const bySousSujet = Object.values(groups).map(g => ({ ...g, pct: g.total ? Math.round((g.correct / g.total) * 100) : 0 }));
            const isTransverse = bySousSujet.length > 1;
            const avgTime = timeCount ? Math.round(timeSum / timeCount) : null;
            const nbErrors = cTotal - cScore;
            return (
                <div onClick={() => setShowCelebration(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10002 /* au-dessus des flèches latérales (.quiz-nav-arrow, 10001) */, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 470, maxHeight: '92vh', overflowY: 'auto' }}>
                        {renderObjectifBilan(true)}
                        <MasteryVerdict
                            pct={cPct} score={cScore} total={cTotal} color={scopeAccent || '#7c3aed'}
                            timeInSeconds={timeInSeconds} avgTime={avgTime} avgTimeOthers={paceOthers}
                            isTransverse={isTransverse} bySousSujet={bySousSujet}
                            nbErrors={nbErrors} modeLabel={isApprentissage ? 'Apprentissage' : 'Classique'}
                            contextLabel={quizInfo?.label}
                            onReviewAll={() => { setShowCelebration(false); setErrOnly(false); }}
                            onReviewErrors={firstWrong >= 0 ? () => { setShowCelebration(false); setErrOnly(true); goToQuestion(firstWrong); } : null}
                            primaryAction={verdictAction}
                            upsell={cPct >= 70 ? verdictUpsell : null}
                        />
                    </div>
                </div>
            );
        })()}
        <div className={`quiz-page ${isReviewer ? '' : 'no-select'}`}>
        <div className="quiz-page-scroll">
            {/* Scope banner — same style as ScopeLanding header */}
            {scopeBanner && (
                <div className="quiz-scope-banner" style={scopeAccent ? { '--scope-accent': scopeAccent } : {}}>
                    {bannerImage && (
                        <div className="quiz-scope-banner-hero">
                            <img src={bannerImage} alt="" className="quiz-scope-banner-img" />
                            <div className="quiz-scope-banner-gradient" />
                        </div>
                    )}
                    <div className="quiz-scope-banner-content">
                        <div className="quiz-scope-banner-info">
                            <span className="quiz-scope-banner-title">{scopeBanner.label}</span>
                            {scopeBanner.description && (
                                <span className="quiz-scope-banner-desc">{scopeBanner.description}</span>
                            )}
                        </div>
                        {bannerCategorie && (
                            <span className="quiz-scope-banner-cat">{bannerCategorie}</span>
                        )}
                    </div>
                </div>
            )}
            {!scopeBanner && bannerCategorie && (
                <div className="quiz-category-bar">{bannerCategorie}</div>
            )}

            {/* Context bar for transversal modes — shows current question's category/subject */}
            {(() => {
                const isTransversal = isTransversalBanner;
                if (!isTransversal || !questions[currentIndex]) return null;
                const q = questions[currentIndex];
                const cat = q.categorie || null;
                const sujet = q.sous_sujet_label || null;
                if (!cat && !sujet) return null;
                return (
                    <div className="quiz-context-bar">
                        {cat && <span className="quiz-context-cat">{cat}</span>}
                        {cat && sujet && <span className="quiz-context-sep">&rsaquo;</span>}
                        {sujet && <span className="quiz-context-sujet">{sujet}</span>}
                    </div>
                );
            })()}

            {isReview && accesRevue?.locked > 0 && (
                <div className="quiz-acces-revue">
                    <span>
                        Ton abonnement est terminé : tu peux revoir cette séance
                        {accesRevue.playable > 0
                            ? <>, et t'entraîner à nouveau sur <strong>{accesRevue.playable} de ses {accesRevue.total} questions</strong> avec « Refaire ».</>
                            : <>, mais pas t'y entraîner à nouveau.</>}
                    </span>
                    {scopeInfoProp?.slug && scopeInfoProp?.content_source !== 'user' && (
                        <button type="button" className="quiz-acces-revue__btn" onClick={allerAbonnement}>Voir les abonnements</button>
                    )}
                </div>
            )}

            <div className="quiz-header">
                <div className="quiz-header-top">
                    <h2>{quizInfo?.label || 'Quiz'}</h2>
                    {(() => {
                        const q = questions[currentIndex];
                        const docInfo = docsMap && q?.id ? docsMap[q.id] : null;
                        const documents = docInfo?.documents || quizInfo?.documents || [];
                        const docPath = docInfo?.doc_path || quizInfo?.doc_path;
                        const docName = docInfo?.doc_name || quizInfo?.doc_name;
                        const isTransversal = isTransversalBanner;
                        const docSujetLabel = isTransversal ? (docInfo?.sous_sujet_label || null) : null;

                        // Priorité multi-doc : si la nouvelle liste est non vide, on l'utilise
                        if (documents.length > 0) {
                            return (
                                <span title={docSujetLabel ? `Sources de : ${docSujetLabel}` : undefined}>
                                    <SujetDocsButton documents={documents} compact />
                                </span>
                            );
                        }

                        // Fallback single-doc legacy
                        return docPath ? (
                            <>
                                <button className="quiz-doc-link" onClick={() => setShowDocViewer(true)}
                                    title={docSujetLabel ? `Document de : ${docSujetLabel}` : (docName || 'Document source')}>
                                    <FileText size={14} />
                                    {docSujetLabel && <span className="quiz-doc-label">{docSujetLabel}</span>}
                                </button>
                                {showDocViewer && (
                                    <DocumentViewer src={docPath} name={docName} onClose={() => setShowDocViewer(false)} />
                                )}
                            </>
                        ) : null;
                    })()}
                    <span className={`quiz-mode-badge ${mode}`}>
                        {isApprentissage ? 'Apprentissage' : 'Classique'}
                    </span>
                </div>
                <div className="quiz-meta">
                    {/* Séance en cours : on peut s'arrêter à tout moment (après confirmation), les réponses données comptent */}
                    {!finished && !isReview && !allAnswered && nbAnswered > 0 && (
                        <button type="button" className="quiz-stop-btn quiz-stop-btn--header" disabled={closing || finalizing} onClick={() => setShowStopConfirm(true)}
                            title={`Enregistre tes ${nbAnswered} réponse${nbAnswered > 1 ? 's' : ''} et affiche ton résultat ; les questions non répondues ne comptent pas`}>
                            <Flag size={13} /> <span className="quiz-stop-btn__label">S’arrêter là</span>
                        </button>
                    )}
                    {activeLimit && !finished && !isReview ? (
                        timeInSeconds < activeLimit
                            ? <span className="quiz-timer" title={`Temps restant sur ${formatTime(activeLimit)}`}>⏱ {formatTime(activeLimit - timeInSeconds)}</span>
                            : <span className="quiz-timer quiz-timer--over" title="Temps max dépassé">+{formatTime(timeInSeconds - activeLimit)}</span>
                    ) : (
                        <span className="quiz-timer">{formatTime(timeInSeconds)}</span>
                    )}
                    {questions.length > 1 && (
                        <button type="button" className="quiz-dots-toggle" onClick={() => setDotsOpen(o => !o)} aria-expanded={dotsOpen}>
                            Questions {dotsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                    )}
                    <ProgressBar current={currentIndex + 1} total={questions.length} />
                    {finished && correctAnswers && (() => {
                        const score = Object.entries(answers).filter(([qId]) =>
                            correctAnswers[qId]?.correct === answers[qId]
                        ).length;
                        const pct = Math.round((score / questions.length) * 100);
                        const color = pct >= 75 ? 'var(--success)' : pct >= 50 ? '#e67e22' : 'var(--danger)';
                        return (
                            <span className="quiz-score-badge" style={{ '--score-color': color }}>
                                <CheckCircle size={13} />
                                {score}/{questions.length} ({pct}%)
                            </span>
                        );
                    })()}
                    {editorialTools && reviewStats && (
                        <span className="quiz-review-stats">
                            <span className="qrs-item qrs-ok" title={`${reviewStats.validated} validée(s)`}>
                                <CheckCircle size={13} />{reviewStats.validated}
                            </span>
                            <span className="qrs-sep">/</span>
                            <span className="qrs-item qrs-warn" title={`${reviewStats.to_check} à vérifier (sans review, conflit, à revoir, rejetée)`}>
                                <AlertTriangle size={13} />{reviewStats.to_check}
                            </span>
                            <span className="qrs-total">sur {reviewStats.total}</span>
                        </span>
                    )}
                </div>
            </div>

            {/* Bilan des options du Mélange : objectif de score et temps max */}
            {renderObjectifBilan()}

            {/* Bascule de revue : toutes les réponses / erreurs uniquement */}
            {inReviewMode && wrongIndices.length > 0 && wrongIndices.length < questions.length && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', margin: '2px 0 8px', fontSize: '0.85em', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Revue :</span>
                    <button className={`btn btn-xs ${!errOnly ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setErrOnly(false)}>
                        Tout ({questions.length})
                    </button>
                    <button className={`btn btn-xs ${errOnly ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => { setErrOnly(true); if (!wrongIndices.includes(safeIndex)) goToQuestion(wrongIndices[0]); }}>
                        Mes erreurs ({wrongIndices.length})
                    </button>
                </div>
            )}

            {/* Mini-nav slider (mobile : repliée tant que « Questions » n'est pas ouvert) */}
            <div className={`quiz-mini-nav${dotsOpen ? ' is-open' : ''}`}>
                <button className="mini-nav-arrow" onClick={() => scrollDots(dotsRef, 'left')}>
                    <ChevronLeft size={18} />
                </button>
                <div className="mini-nav-dots" ref={dotsRef}>
                    {reviewIndices.map((i) => {
                        const q = questions[i];
                        const hasAnswer = !!answers[q.id];
                        const showResult = isReview || finished || (isApprentissage && feedbackShown[q.id]);
                        const isCorrect = showResult && hasAnswer && correctAnswers?.[q.id]?.correct === answers[q.id];
                        const isIncorrect = showResult && hasAnswer && correctAnswers?.[q.id]?.correct !== answers[q.id];

                        return (
                            <button
                                key={q.id}
                                className={`dot ${i === currentIndex ? 'active' : ''} ${hasAnswer ? 'answered' : ''} ${isCorrect ? 'correct' : ''} ${isIncorrect ? 'incorrect' : ''}`}
                                onClick={() => goToQuestion(i)}
                            >
                                {i + 1}
                            </button>
                        );
                    })}
                </div>
                <button className="mini-nav-arrow" onClick={() => scrollDots(dotsRef, 'right')}>
                    <ChevronRight size={18} />
                </button>
            </div>

            {/* Document reminder — shown when a document is associated */}
            {(() => {
                const q = questions[currentIndex];
                const docInfo = docsMap && q?.id ? docsMap[q.id] : null;
                const docPath = docInfo?.doc_path || quizInfo?.doc_path;
                const docName = docInfo?.doc_name || quizInfo?.doc_name;
                if (!docPath || showCurrentResult) return null;
                return (
                    <div className="quiz-doc-reminder" onClick={() => setShowDocViewer(true)}>
                        <FileText size={16} />
                        <span>Un <strong>document</strong> est associé à cette question. Consultez-le avant de répondre.</span>
                        <button className="btn btn-xs btn-primary">Voir le document</button>
                    </div>
                );
            })()}

            <QuestionCard
                question={currentQuestion}
                selectedAnswer={answers[currentQuestion.id]}
                onAnswer={(ans) => handleAnswer(currentQuestion.id, ans)}
                allowDontKnow={origin === 'audit-parcours' || origin === 'audit-diagnostic'}
                showResult={showCurrentResult}
                correctAnswer={correctAnswers?.[currentQuestion.id]?.correct}
                apprentissageFeedback={isApprentissage}
            />

            {showCurrentResult && <ContentChangeNotice change={currentQuestion._contentChange} />}
            {showCurrentResult && currentQuestion._versionChanged && (
                <div className="quiz-version-notice">
                    <History size={13} />
                    Version répondue : v{currentQuestion._answeredVersion} — cette question a été modifiée depuis (v{currentQuestion._currentVersion} actuelle)
                </div>
            )}
            {showCurrentResult && correctAnswers?.[currentQuestion.id]?.explication && (
                <div className="explication">
                    <h4>Explication</h4>
                    <RichContent blocks={correctAnswers?.[currentQuestion.id]?.explication} />
                </div>
            )}
            {showCurrentResult && afterExplanation?.(currentQuestion)}

            {/* Bouton Suivant en mode apprentissage après feedback */}
            {isApprentissage && currentFeedback && !finished && currentIndex < questions.length - 1 && (
                <button className="btn btn-primary quiz-next-btn" onClick={nextQuestion}>
                    Question suivante <ChevronRight size={16} />
                </button>
            )}

            {/* Edit panel — custom questions OR admin/reviewer on editorial */}
            {showCurrentResult && (isCustomProp || editorialTools) && (
                <QuestionEditPanel
                    question={currentQuestion}
                    correctData={correctAnswers?.[currentQuestion.id]}
                    source={isCustomProp ? 'custom' : 'official'}
                    onSaved={(updated) => updateQuestion(currentQuestion.id, updated)}
                    onDeleted={isCustomProp ? (id) => removeQuestion(id) : undefined}
                />
            )}

            {editorialTools && <QuizReviewPanel question={currentQuestion} user={user} />}

            {/* Side arrows moved outside quiz-page to avoid mask-image clipping */}

            {/* Mobile : précédente / suivante sous le pouce, à la place des flèches latérales (masquées en mobile).
                En revue et en fin d'apprentissage, la barre du bas a déjà ses propres boutons. */}
            {questions.length > 1 && !finished && !isReview && !(isApprentissage && currentFeedback) && (
                <div className="quiz-step-nav">
                    <button type="button" className="btn btn-ghost" onClick={prevQuestion} disabled={currentIndex === 0}>
                        <ChevronLeft size={18} /> Précédente
                    </button>
                    <span className="quiz-step-nav__count">{currentIndex + 1} / {questions.length}</span>
                    <button type="button" className="btn btn-ghost" onClick={nextQuestion} disabled={currentIndex >= questions.length - 1}>
                        Suivante <ChevronRight size={18} />
                    </button>
                </div>
            )}

            {/* Bottom action bar */}
            <div className="quiz-nav">
                {finished && isApprentissage ? (
                    /* Apprentissage finished: nav + terminer to close */
                    <>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={goReviewPrev}
                            disabled={atReviewStart}
                        >
                            <ChevronLeft size={16} /> Précédente
                        </button>
                        <button className="btn btn-primary" onClick={handleBack}>
                            Terminer
                        </button>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={goReviewNext}
                            disabled={atReviewEnd}
                        >
                            Suivante <ChevronRight size={16} />
                        </button>
                    </>
                ) : finished || isReview ? (
                    <>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={goReviewPrev}
                            disabled={atReviewStart}
                        >
                            <ChevronLeft size={16} /> Précédente
                        </button>
                        <button className="btn btn-secondary" onClick={handleBack}>
                            Retour
                        </button>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={goReviewNext}
                            disabled={atReviewEnd}
                        >
                            Suivante <ChevronRight size={16} />
                        </button>
                    </>
                ) : allAnswered && !finished ? (
                    /* All answered but not finished yet — show Terminer */
                    <button className="btn btn-success" onClick={() => handleFinish()}>
                        Terminer
                    </button>
) : nbAnswered > 0 ? (
                    /* « S'arrêter là » est dans l'en-tête, à côté du chrono */
                    null
                ) : !finished && !isReview && questions.length > 0 ? (
                    /* Aucune réponse : la séance peut être annulée, il n'y a rien à enregistrer */
                    <button className="btn btn-ghost btn-sm" disabled={closing || finalizing} onClick={handleCancelEmpty}
                        title="Aucune réponse donnée : la séance est supprimée, rien n'est perdu">
                        <X size={14} /> Annuler la séance
                    </button>
                ) : null}
            </div>

            {/* Pause overlay on inactivity */}
            {paused && !finished && !isReview && (
                <div className="quiz-pause-overlay" onClick={() => setPaused(false)}>
                    <div className="quiz-pause-content">
                        <div className="quiz-pause-icon">⏸</div>
                        <h3>Quiz en pause</h3>
                        <p>Le timer est arrêté suite à une inactivité.</p>
                        <button className="btn btn-primary" onClick={() => setPaused(false)}>Reprendre</button>
                    </div>
                </div>
            )}

            {/* Confirmation de « S'arrêter là » */}
            {showStopConfirm && !finished && !isReview && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={() => setShowStopConfirm(false)}>
                    <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card-bg, #fff)', borderRadius: 16, padding: '24px 26px', maxWidth: 400, width: '100%', textAlign: 'center', border: '1px solid var(--card-border)', boxShadow: '0 12px 44px rgba(0,0,0,0.28)' }}>
                        <Flag size={30} style={{ color: '#e67e22', margin: '0 auto 8px' }} />
                        <h3 style={{ margin: '0 0 6px' }}>T’arrêter là ?</h3>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', margin: '0 0 18px' }}>
                            Tes {nbAnswered} réponse{nbAnswered > 1 ? 's' : ''} {nbAnswered > 1 ? 'sont enregistrées' : 'est enregistrée'} et ton résultat s’affiche.
                            {questions.length - nbAnswered > 0 && <> {questions.length - nbAnswered === 1 ? 'La question sans réponse ne compte pas.' : `Les ${questions.length - nbAnswered} questions sans réponse ne comptent pas.`}</>}
                            {activeTarget ? ' Ton objectif ne sera pas évalué.' : ''}
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button className="btn btn-primary" onClick={() => setShowStopConfirm(false)} autoFocus>
                                Continuer la séance
                            </button>
                            <button className="btn btn-secondary quiz-stop-btn" disabled={closing || finalizing} onClick={() => { setShowStopConfirm(false); handleFinish(true); }}>
                                <Flag size={14} /> M’arrêter là
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Temps max atteint (Mélange classique) : valider maintenant ou poursuivre */}
            {showTimeUp && !finished && !isReview && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: 'var(--card-bg, #fff)', borderRadius: 16, padding: '24px 26px', maxWidth: 400, width: '100%', textAlign: 'center', border: '1px solid var(--card-border)', boxShadow: '0 12px 44px rgba(0,0,0,0.28)' }}>
                        <div style={{ fontSize: '2em', marginBottom: 6 }}>⏱</div>
                        <h3 style={{ margin: '0 0 6px' }}>Le temps est atteint</h3>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', margin: '0 0 18px' }}>
                            {nbAnswered}/{questions.length} réponse{nbAnswered > 1 ? 's' : ''} en {formatTime(activeLimit || 0)}.
                            Valide pour voir ton résultat (une question sans réponse compte comme fausse), ou poursuis : le dépassement sera indiqué dans ton bilan.
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button className="btn btn-success" disabled={closing || finalizing} onClick={() => { setShowTimeUp(false); handleFinish(); }}>
                                Valider
                            </button>
                            <button className="btn btn-secondary" onClick={() => setShowTimeUp(false)}>
                                Poursuivre
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Popup de validation — toutes les questions répondues (classique) */}
            {showValidatePrompt && !finished && !isReview && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={() => setShowValidatePrompt(false)}>
                    <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card-bg, #fff)', borderRadius: 16, padding: '24px 26px', maxWidth: 380, width: '100%', textAlign: 'center', border: '1px solid var(--card-border)', boxShadow: '0 12px 44px rgba(0,0,0,0.28)' }}>
                        <CheckCircle size={34} style={{ color: 'var(--success, #22c55e)', margin: '0 auto 8px' }} />
                        <h3 style={{ margin: '0 0 6px' }}>Toutes les questions sont répondues</h3>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', margin: '0 0 18px' }}>
                            Valide pour voir ton résultat, ou continue à modifier tes réponses.
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button className="btn btn-success" onClick={() => { setShowValidatePrompt(false); handleFinish(); }}>
                                Valider mes réponses
                            </button>
                            <button className="btn btn-secondary" onClick={() => setShowValidatePrompt(false)}>
                                Modifier mes réponses
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </div>
        {/* Fixed side arrows — outside quiz-page to avoid mask-image clipping.
            En revue, elles suivent le filtre (toutes / erreurs uniquement). */}
        {questions.length > 1 && (
            <>
                <button
                    className="quiz-nav-arrow quiz-nav-arrow--left"
                    onClick={inReviewMode ? goReviewPrev : prevQuestion}
                    disabled={inReviewMode ? atReviewStart : currentIndex === 0}
                    aria-label="Question précédente"
                >
                    <ChevronLeft size={28} />
                </button>
                <button
                    className="quiz-nav-arrow quiz-nav-arrow--right"
                    onClick={inReviewMode ? goReviewNext : (currentIndex < questions.length - 1 ? nextQuestion : undefined)}
                    disabled={inReviewMode ? atReviewEnd : currentIndex >= questions.length - 1}
                    aria-label="Question suivante"
                >
                    <ChevronRight size={28} />
                </button>
            </>
        )}
        </>
    );
}
